import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
} from 'react';
import { Slot } from 'radix-ui';
import { Keycap } from '../primitives/Display.tsx';
import { loadOverlays, useOpenedOnce, useOverlayKit } from './lazy.ts';

// Radix Tooltip's provider, root and trigger state, ported so the trigger needs no Radix code; the floating content
// is `TooltipLayer` in the lazy overlay kit (lazy.ts). Delay groups, hoverable content, the one-open-tooltip rule and
// `aria-describedby` / `data-state` on the trigger behave as in Radix.

interface TooltipProviderValue {
  delayDuration: number;
  isOpenDelayedRef: RefObject<boolean>;
  isPointerInTransitRef: RefObject<boolean>;
  onOpen: () => void;
  onClose: () => void;
  onPointerInTransitChange: (inTransit: boolean) => void;
}

function providerValue(delayDuration: number, skipDelayDuration: number, skipTimer: RefObject<number>): TooltipProviderValue {
  const isOpenDelayedRef = { current: true };
  const isPointerInTransitRef = { current: false };
  return {
    delayDuration,
    isOpenDelayedRef,
    isPointerInTransitRef,
    onOpen: () => {
      if (skipDelayDuration <= 0) return;
      window.clearTimeout(skipTimer.current);
      isOpenDelayedRef.current = false;
    },
    onClose: () => {
      if (skipDelayDuration <= 0) return;
      window.clearTimeout(skipTimer.current);
      skipTimer.current = window.setTimeout(() => (isOpenDelayedRef.current = true), skipDelayDuration);
    },
    onPointerInTransitChange: inTransit => {
      isPointerInTransitRef.current = inTransit;
    },
  };
}

const TooltipContext = createContext<TooltipProviderValue | null>(null);
// tooltips outside any provider share one with the default delays
const fallbackProvider = providerValue(400, 200, { current: 0 });

export function TooltipProvider({
  children,
  delayDuration = 400,
  skipDelayDuration = 200,
}: {
  children: ReactNode;
  /** hover time before a tooltip opens */
  delayDuration?: number;
  /** after a tooltip closed, others open at once within this time */
  skipDelayDuration?: number;
}) {
  const skipTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(skipTimer.current), []);
  const value = useMemo(() => providerValue(delayDuration, skipDelayDuration, skipTimer), [delayDuration, skipDelayDuration]);
  return <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>;
}

/** The static tooltip bubble (design previews / custom positioning). */
export function TooltipBubble({ children, keys }: { children?: ReactNode; keys?: string[] }) {
  return (
    <span className="md-tip" role="tooltip">
      {children}
      {keys ? <Keycap keys={keys} /> : null}
    </span>
  );
}

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

export interface TooltipRootProps {
  /** the trigger — must accept a ref */
  children: ReactElement;
  /** content of the floating surface */
  content: ReactNode;
  /** controlled open state (e.g. paused while dragging) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: Side;
  align?: Align;
  sideOffset?: number;
  collisionPadding?: number;
  className: string;
  /** announced instead of the content (rich previews) */
  'aria-label'?: string;
}

const skip = (e: SyntheticEvent) => e.defaultPrevented;

/** Hover / focus tooltip with any floating surface (`Tooltip` below, the rail's community preview). */
export function TooltipRoot({
  children,
  content,
  open: openProp,
  onOpenChange,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  collisionPadding = 8,
  className,
  'aria-label': ariaLabel,
}: TooltipRootProps) {
  const provider = useContext(TooltipContext) ?? fallbackProvider;
  const contentId = useId();
  const trigger = useRef<HTMLElement | null>(null);
  const openTimer = useRef(0);
  const wasOpenDelayed = useRef(false);
  const pointerDown = useRef(false);
  const pointerMoveOpened = useRef(false);
  const [local, setLocal] = useState(false);
  const open = openProp ?? local;

  // Radix runs these on every actual change (another tooltip closes, the delay group updates). They run before the
  // state update here, so a tooltip whose lazy layer mounts together with its first open cannot close itself.
  const value = useRef(open);
  const latest = useRef({ provider, onOpenChange, controlled: openProp !== undefined });
  useLayoutEffect(() => {
    value.current = open;
    latest.current = { provider, onOpenChange, controlled: openProp !== undefined };
  });
  const setOpen = useCallback((next: boolean) => {
    if (next === value.current) return;
    const l = latest.current;
    if (next) {
      l.provider.onOpen();
      document.dispatchEvent(new CustomEvent('tooltip.open'));
    } else l.provider.onClose();
    value.current = next;
    if (!l.controlled) setLocal(next);
    l.onOpenChange?.(next);
  }, []);

  const handleOpen = useCallback(() => {
    window.clearTimeout(openTimer.current);
    openTimer.current = 0;
    wasOpenDelayed.current = false;
    setOpen(true);
  }, [setOpen]);
  const handleClose = useCallback(() => {
    window.clearTimeout(openTimer.current);
    openTimer.current = 0;
    setOpen(false);
  }, [setOpen]);
  const handleDelayedOpen = () => {
    window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(() => {
      wasOpenDelayed.current = true;
      setOpen(true);
      openTimer.current = 0;
    }, provider.delayDuration);
  };
  useEffect(
    () => () => {
      window.clearTimeout(openTimer.current);
      openTimer.current = 0;
    },
    [],
  );
  // derived once per open change, as in Radix
  const state = useMemo(() => (open ? (wasOpenDelayed.current ? 'delayed-open' : 'instant-open') : 'closed'), [open]);

  const onPointerUp = useCallback(() => {
    pointerDown.current = false;
  }, []);
  useEffect(() => () => document.removeEventListener('pointerup', onPointerUp), [onPointerUp]);

  const kit = useOverlayKit(open);
  const mounted = useOpenedOnce(open);
  return (
    <>
      <Slot.Root
        ref={trigger}
        aria-describedby={open ? contentId : undefined}
        data-state={state}
        onPointerMove={(e: PointerEvent) => {
          if (skip(e) || e.pointerType === 'touch') return;
          if (!pointerMoveOpened.current && !provider.isPointerInTransitRef.current) {
            void loadOverlays().catch(() => undefined);
            if (provider.isOpenDelayedRef.current) handleDelayedOpen();
            else handleOpen();
            pointerMoveOpened.current = true;
          }
        }}
        onPointerLeave={(e: PointerEvent) => {
          if (skip(e)) return;
          // hoverable content: the lazy layer's grace area decides (Radix `disableHoverableContent: false`) — before the
          // kit is there nobody would, so the tooltip closes
          if (kit) {
            window.clearTimeout(openTimer.current);
            openTimer.current = 0;
          } else handleClose();
          pointerMoveOpened.current = false;
        }}
        onPointerDown={(e: PointerEvent) => {
          if (skip(e)) return;
          if (open) handleClose();
          pointerDown.current = true;
          document.addEventListener('pointerup', onPointerUp, { once: true });
        }}
        onFocus={(e: SyntheticEvent) => {
          if (skip(e)) return;
          if (!pointerDown.current) handleOpen();
        }}
        onBlur={(e: SyntheticEvent) => {
          if (!skip(e)) handleClose();
        }}
        onClick={(e: SyntheticEvent) => {
          if (!skip(e)) handleClose();
        }}
      >
        {children}
      </Slot.Root>
      {kit && mounted ? (
        <kit.TooltipLayer
          open={open}
          state={state}
          contentId={contentId}
          trigger={trigger}
          onClose={handleClose}
          onPointerInTransitChange={provider.onPointerInTransitChange}
          disableHoverableContent={false}
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          className={className}
          aria-label={ariaLabel}
        >
          {content}
        </kit.TooltipLayer>
      ) : null}
    </>
  );
}

export interface TooltipProps {
  /** tooltip text */
  content: ReactNode;
  /** optional keyboard shortcut shown as keycaps */
  keys?: string[];
  side?: Side;
  align?: Align;
  /** the trigger — must accept a ref (button, link, IconButton …) */
  children: ReactElement;
  disabled?: boolean;
}

/** Hover / focus tooltip with the MOODS `.md-tip` look (Radix Tooltip positioning & a11y, loaded lazily). */
export function Tooltip({ content, keys, side = 'top', align = 'center', children, disabled }: TooltipProps) {
  if (disabled) return children;
  return (
    <TooltipRoot
      side={side}
      align={align}
      sideOffset={6}
      collisionPadding={8}
      className="md-tip md-tip--floating"
      content={
        <>
          {content}
          {keys ? <Keycap keys={keys} /> : null}
        </>
      }
    >
      {children}
    </TooltipRoot>
  );
}
